import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  UserIcon,
  AcademicCapIcon,
  CalendarIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon,
  BriefcaseIcon,
  BookOpenIcon,
  UserPlusIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../api/client';
import { FireLoader } from '../components/Loader';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  showConfirm,
  showSuccess,
  showError,
  showWarning,
  showInfo,
} from '../utils/alert';
import { useAuth } from '../context/AuthContext';
import { socket } from '../services/socket';

const STUDENTS_PER_PAGE = 10;

const STUDENT_STAT_COLORS = {
  total: '#6366f1',
  male: '#22c55e',
  female: '#ec4899',
};

const normalizeGender = (gender) =>
  String(gender ?? '').trim().toLowerCase();

const escapeHtml = (value) =>
  String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

// ─── Helper: Format student full name as "SURNAME, First Middle" ───
const formatFullName = (student) => {
  const surname = student.last_name || student.lastName || '';
  const firstName = student.first_name || student.firstName || '';
  const middleName = student.middle_name || student.middleName || '';
  if (!surname && !firstName) return 'N/A';
  const nameParts = [];
  if (surname) nameParts.push(surname + ',');
  if (firstName) nameParts.push(firstName);
  if (middleName) nameParts.push(middleName);
  return nameParts.join(' ');
};

// ─── Helper: Get student initials from surname and first name ───
const getStudentInitials = (student) => {
  const surname = student.last_name || student.lastName || '';
  const firstName = student.first_name || student.firstName || '';
  return `${surname.charAt(0) || ''}${firstName.charAt(0) || ''}`.toUpperCase() || 'ST';
};

export default function ClassDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, planAccess } = useAuth();
  const isStarterPlan =
    String(planAccess?.plan?.name || '').trim().toLowerCase() === 'starter';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isTeacher = user?.role === 'teacher';

  // ---------- State ----------
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false);
  const [schoolName, setSchoolName] = useState('School');

  // Students
  const [students, setStudents] = useState([]);
  const [studentPage, setStudentPage] = useState(1);

  // Teachers
  const [assignedTeachers, setAssignedTeachers] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [pendingTeacherId, setPendingTeacherId] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Subjects
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [isAssigningSubjects, setIsAssigningSubjects] = useState(false);
  const [isRemovingSubject, setIsRemovingSubject] = useState(false);
  const [pendingSubjectId, setPendingSubjectId] = useState(null);
  const [showAssignSubjectModal, setShowAssignSubjectModal] = useState(false);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [subjectTeachers, setSubjectTeachers] = useState({});

  // Ref for background updates
  const fetchAllDataRef = useRef();

  // ---------- Combined fetch function with background flag ----------
  const fetchAllData = useCallback(async (options = { background: false }) => {
    try {
      if (!options.background) setLoading(true);

      // Fetch class details
      const classRes = await apiClient.get(`/classes/${id}`);
      setClassData(classRes.data.data || classRes.data);

      // Fetch teachers
      const teachersRes = await apiClient.get(`/classes/${id}/teachers`);
      const teachersData = teachersRes.data?.data || teachersRes.data || [];
      setAssignedTeachers(Array.isArray(teachersData) ? teachersData : []);

      // Fetch all teachers (for assignment modal)
      const allTeachersRes = await apiClient.get('/teachers', {
        params: { limit: 100, status: 'active' }
      });
      const allTeachersData = allTeachersRes.data?.data || allTeachersRes.data || [];
      setAllTeachers(Array.isArray(allTeachersData) ? allTeachersData : []);

      // Fetch students
      const studentsRes = await apiClient.get(`/classes/${id}/students`, {
        // Load every student assigned to this class. The backend still applies
        // the school tenant and class filters before returning these records.
        params: { page: 1, limit: 1000 }
      });
      let studentsData = studentsRes.data?.data || studentsRes.data || [];
      studentsData = Array.isArray(studentsData) ? studentsData : [];
      // Sort by surname first, then first name (matches Students page)
      studentsData.sort((a, b) => {
        const lastNameA = (a.last_name || a.lastName || '').toUpperCase();
        const lastNameB = (b.last_name || b.lastName || '').toUpperCase();
        if (lastNameA < lastNameB) return -1;
        if (lastNameA > lastNameB) return 1;
        const firstNameA = (a.first_name || a.firstName || '').toUpperCase();
        const firstNameB = (b.first_name || b.firstName || '').toUpperCase();
        if (firstNameA < firstNameB) return -1;
        if (firstNameA > firstNameB) return 1;
        return 0;
      });
      setStudents(studentsData);

      // Fetch subjects
      const subjectsRes = await apiClient.get(`/subjects/classes/${id}/subjects`);
      const subjectsData = subjectsRes.data?.data || subjectsRes.data || [];
      const subjects = Array.isArray(subjectsData) ? subjectsData : [];
      setAssignedSubjects(subjects);

      // Fetch subject teachers
      const teacherMap = {};
      for (const subject of subjects) {
        try {
          const res = await apiClient.get(`/subjects/${subject.id}/teachers`, {
            params: { class_id: id }
          });
          const data = res.data?.data || res.data || [];
          teacherMap[subject.id] = data.map(item => item.teacher);
        } catch {
          teacherMap[subject.id] = [];
        }
      }
      setSubjectTeachers(teacherMap);

      // Fetch all subjects for assignment modal
      const allSubjectsRes = await apiClient.get('/subjects');
      const allSubjectsData = allSubjectsRes.data?.data || allSubjectsRes.data || [];
      setAllSubjects(Array.isArray(allSubjectsData) ? allSubjectsData : []);

    } catch (error) {
      if (!options.background) {
        await showError({ text: 'Failed to fetch class details' });
      }
      console.error('Error fetching class details:', error);
    } finally {
      if (!options.background) setLoading(false);
    }
  }, [id]);

  // Store fetch function in ref
  useEffect(() => {
    fetchAllDataRef.current = fetchAllData;
  }, [fetchAllData]);

  // ---------- Lifecycle ----------
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    let active = true;
    const loadSchoolName = async () => {
      const authName = user?.school?.name || user?.school_name || user?.schoolName;
      const [profileResponse, meResponse] = await Promise.all([
        apiClient.get('/schools/profile').catch(() => null),
        apiClient.get('/users/me').catch(() => null),
      ]);
      const profile = profileResponse?.data?.data || profileResponse?.data?.school || profileResponse?.data || {};
      const me = meResponse?.data?.data || meResponse?.data || {};
      const resolvedName =
        profile.name || profile.schoolName || profile.school?.name ||
        me.school?.name || me.school_name || me.schoolName ||
        me.user?.school?.name || me.user?.school_name || authName;
      if (active && resolvedName) setSchoolName(resolvedName);
    };
    loadSchoolName();
    return () => { active = false; };
  }, [user]);

  useEffect(() => {
    setStudentPage(1);
  }, [id]);

  useEffect(() => {
    const pages = Math.max(
      1,
      Math.ceil(students.length / STUDENTS_PER_PAGE)
    );
    setStudentPage((page) => Math.min(page, pages));
  }, [students.length]);

  // ---------- Socket.IO listeners (background updates) ----------
  useEffect(() => {
    const handleUpdate = (event) => {
      console.log('📡 ClassDetails update received:', event);
      if (fetchAllDataRef.current) {
        fetchAllDataRef.current({ background: true });
      }
    };

    socket.on('class:updated', handleUpdate);
    socket.on('student:updated', handleUpdate);
    socket.on('teacher:updated', handleUpdate);
    socket.on('subject:updated', handleUpdate);

    return () => {
      socket.off('class:updated', handleUpdate);
      socket.off('student:updated', handleUpdate);
      socket.off('teacher:updated', handleUpdate);
      socket.off('subject:updated', handleUpdate);
    };
  }, []);

  // ---------- Optimistic Teacher Assignment (Admin only) ----------
  const handleAssignTeacher = async () => {
    if (!isAdmin) return;
    if (!selectedTeacherId) {
      await showInfo({ title: 'Select a teacher', text: 'Please choose a teacher to assign.' });
      return;
    }

    const newTeacher = allTeachers.find(t => t.id === selectedTeacherId);
    if (!newTeacher) return;

    setIsAssigning(true);
    setPendingTeacherId(selectedTeacherId);
    const previousTeachers = [...assignedTeachers];

    setAssignedTeachers([...assignedTeachers, newTeacher]);
    setShowAssignModal(false);
    setSelectedTeacherId(null);
    setSearchTerm('');

    try {
      await apiClient.post(`/classes/${id}/assign-teacher`, {
        teacher_id: parseInt(selectedTeacherId),
        is_primary: true
      });
      await showSuccess({ text: 'Teacher assigned successfully!' });
      await fetchAllData({ background: true });
    } catch (error) {
      setAssignedTeachers(previousTeachers);
      const msg = error.response?.data?.message || 'Failed to assign teacher';
      if (msg.toLowerCase().includes('already has a teacher')) {
        await showWarning({
          title: 'Already Assigned',
          text: 'This class already has a teacher. Please remove the current teacher first.',
        });
      } else {
        await showError({ text: msg });
      }
    } finally {
      setIsAssigning(false);
      setPendingTeacherId(null);
    }
  };

  // ---------- Optimistic Teacher Removal (Admin only) ----------
  const handleRemoveTeacher = async (teacherId) => {
    if (!isAdmin) return;
    const confirmed = await showConfirm({
      title: 'Remove Teacher?',
      text: 'Are you sure you want to remove this teacher from the class?',
      confirmText: 'Yes, remove',
      icon: 'warning',
    });
    if (!confirmed) return;

    const previousTeachers = [...assignedTeachers];
    setAssignedTeachers(assignedTeachers.filter(t => t.id !== teacherId));
    setIsRemoving(true);
    setPendingTeacherId(teacherId);

    try {
      await apiClient.delete(`/classes/${id}/teacher`, { data: { teacherId } });
      await showSuccess({ text: 'Teacher removed!' });
      await fetchAllData({ background: true });
    } catch (error) {
      setAssignedTeachers(previousTeachers);
      await showError({ text: error.response?.data?.message || 'Failed to remove teacher' });
    } finally {
      setIsRemoving(false);
      setPendingTeacherId(null);
    }
  };

  // ---------- Optimistic Subject Assignment (Admin only) ----------
  const handleAssignSubjects = async () => {
    if (!isAdmin) return;
    if (selectedSubjectIds.length === 0) {
      await showInfo({ title: 'Select subjects', text: 'Please choose at least one subject.' });
      return;
    }

    const classId = parseInt(id);
    if (isNaN(classId) || classId <= 0) {
      await showError({ text: 'Invalid class ID' });
      return;
    }

    const currentIds = assignedSubjects.map(s => s.id);
    const combinedIds = [...new Set([...currentIds, ...selectedSubjectIds])];
    const optimisticSubjects = allSubjects.filter(s => combinedIds.includes(s.id));
    const previousSubjects = [...assignedSubjects];
    const subjectsBeingAdded = [...selectedSubjectIds];

    setIsAssigningSubjects(true);
    setAssignedSubjects(optimisticSubjects);
    setShowAssignSubjectModal(false);
    setSelectedSubjectIds([]);

    try {
      const response = await apiClient.post('/subjects/assign', {
        class_id: classId,
        subject_ids: subjectsBeingAdded,
      });
      await showSuccess({
        text:
          response.data?.message ||
          `${subjectsBeingAdded.length} subject(s) assigned!`,
      });
      await fetchAllData({ background: true });
    } catch (error) {
      setAssignedSubjects(previousSubjects);
      await showError({ text: error.response?.data?.message || 'Failed to assign subjects' });
    } finally {
      setIsAssigningSubjects(false);
    }
  };

  // ---------- Optimistic Subject Removal (Admin only) ----------
  const handleRemoveSubject = async (subjectId) => {
    if (!isAdmin) return;
    const confirmed = await showConfirm({
      title: 'Remove Subject?',
      text: 'Are you sure you want to remove this subject from the class?',
      confirmText: 'Yes, remove',
      icon: 'warning',
    });
    if (!confirmed) return;

    const previousSubjects = [...assignedSubjects];
    setIsRemovingSubject(true);
    setPendingSubjectId(subjectId);
    setAssignedSubjects(assignedSubjects.filter(s => s.id !== subjectId));

    try {
      const classId = parseInt(id);
      await apiClient.delete(
        `/subjects/classes/${classId}/subjects/${subjectId}`,
      );
      await showSuccess({ text: 'Subject removed!' });
      await fetchAllData({ background: true });
    } catch (error) {
      setAssignedSubjects(previousSubjects);
      await showError({ text: error.response?.data?.message || 'Failed to remove subject' });
    } finally {
      setIsRemovingSubject(false);
      setPendingSubjectId(null);
    }
  };

  // ---------- Other actions (Admin only) ----------
  const handleDeleteClass = async () => {
    if (!isAdmin) return;
    const confirmed = await showConfirm({
      title: 'Delete Class?',
      text: `Are you sure you want to delete "${classData?.name}"? This cannot be undone.`,
      confirmText: 'Yes, delete',
      icon: 'error',
    });
    if (!confirmed) return;
    try {
      await apiClient.delete(`/classes/${id}`);
      await showSuccess({ text: 'Class deleted' });
      navigate('/app/classes');
    } catch (error) {
      await showError({ text: 'Failed to delete class' });
    }
  };

  // ---------- Helpers ----------
  const getFullName = (teacher) => {
    return `${teacher.first_name || ''} ${teacher.middle_name || ''} ${teacher.last_name || ''}`.trim();
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-gray-100 text-gray-700',
      archived: 'bg-yellow-100 text-yellow-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Active',
      inactive: 'Inactive',
      archived: 'Archived',
    };
    return labels[status] || status;
  };

  const handlePrintClassDetails = () => {
    if (isStarterPlan) {
      return showWarning({
        title: 'PDF Download Not Available',
        text: 'PDF downloads are not included in the Starter plan. Upgrade to Basic or a higher plan to download PDFs.',
      });
    }

    if (!classData) return;
    setIsPrinting(true);
    try {
      const safeSchoolName = escapeHtml(schoolName || 'School');
      const maleCount = students.filter(
        (student) => normalizeGender(student.gender) === 'male'
      ).length;
      const femaleCount = students.filter(
        (student) => normalizeGender(student.gender) === 'female'
      ).length;
      const totalCount = students.length;
      const chartMaximum = Math.max(totalCount, maleCount, femaleCount, 1);
      const chartBars = [
        { label: 'Total', value: totalCount, color: STUDENT_STAT_COLORS.total },
        { label: 'Male', value: maleCount, color: STUDENT_STAT_COLORS.male },
        { label: 'Female', value: femaleCount, color: STUDENT_STAT_COLORS.female },
      ].map((bar) => `
        <div class="chart-column">
          <div class="chart-value">${bar.value}</div>
          <div class="chart-bar-area">
            <div class="chart-bar" style="height:${bar.value ? Math.max((bar.value / chartMaximum) * 100, 4) : 0}%;background:${bar.color};"></div>
          </div>
          <div class="chart-label"><span class="legend-dot" style="background:${bar.color};"></span>${bar.label}</div>
        </div>
      `).join('');
      const teacherNames = assignedTeachers.length
        ? assignedTeachers.map((teacher) => escapeHtml(getFullName(teacher))).join(', ')
        : 'No teacher assigned';
      const subjectRows = assignedSubjects.map((subject) => {
        const teachers = subjectTeachers[subject.id] || [];
        const names = teachers.length
          ? teachers.map((teacher) => escapeHtml(getFullName(teacher))).join(', ')
          : 'No teacher assigned';
        return `<tr><td><strong>${escapeHtml(subject.name || 'Unnamed Subject')}</strong></td><td>${names}</td></tr>`;
      }).join('');
      const studentRows = students.map((student, index) => `
        <tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(formatFullName(student))}</td>
          <td>${escapeHtml(student.admission_number || 'N/A')}</td>
          <td style="text-transform:capitalize;">${escapeHtml(student.gender || 'N/A')}</td>
          <td>${escapeHtml(student.email || 'N/A')}</td>
        </tr>
      `).join('');
      const html = `<!DOCTYPE html>
        <html><head><meta charset="utf-8"><title>${safeSchoolName} - ${escapeHtml(classData.name)} Details</title>
        <style>
          *{box-sizing:border-box} @page{size:A4 portrait;margin:0}
          body{font-family:Arial,sans-serif;color:#1f2937;background:#fff;padding:11mm 12mm;font-size:10px}
          .watermark{position:fixed;right:18mm;bottom:25mm;transform:rotate(-24deg);font-size:36px;font-weight:700;color:#1e3a5f;opacity:.045;z-index:-1}
          .header{display:flex;justify-content:space-between;gap:20px;border-bottom:3px solid #1e3a5f;padding-bottom:9px;margin-bottom:12px}
          .header h1{font-size:19px;color:#1e3a5f;margin:0 0 3px}.header p{margin:0;color:#64748b}.meta{text-align:right;color:#64748b;line-height:1.5}
          .title{text-align:center;font-size:15px;color:#1e3a5f;margin:12px 0;text-transform:uppercase}
          .summary{display:grid;grid-template-columns:repeat(3,1fr);gap:7px;margin-bottom:10px}
          .summary div{border:1px solid #dbe3ec;border-radius:5px;padding:7px}.summary span{display:block;color:#64748b;font-size:8px;text-transform:uppercase;margin-bottom:3px}.summary strong{font-size:10px}
          .section{border:1px solid #dbe3ec;border-radius:6px;padding:10px;margin-top:10px;break-inside:avoid}.section h2{font-size:12px;color:#1e3a5f;margin:0 0 8px;border-bottom:1px solid #e5e7eb;padding-bottom:5px}
          .chart{height:185px;display:flex;justify-content:center;align-items:flex-end;gap:42px;padding:10px 28px 0;border-left:1px solid #cbd5e1;border-bottom:1px solid #cbd5e1;margin:4px 12px 12px}.chart-column{width:82px;height:165px;display:flex;flex-direction:column;align-items:center;justify-content:flex-end}.chart-value{height:18px;font-weight:700;font-size:11px;color:#334155}.chart-bar-area{height:120px;width:58px;display:flex;align-items:flex-end;justify-content:center}.chart-bar{width:42px;border-radius:4px 4px 0 0;box-shadow:0 1px 2px rgba(15,23,42,.16)}.chart-label{height:27px;padding-top:7px;font-weight:600;white-space:nowrap}.legend-dot{display:inline-block;width:7px;height:7px;border-radius:50%;margin-right:5px}
          .subjects-table th:first-child,.subjects-table td:first-child{width:50%}.subjects-table th:nth-child(2){width:50%}
          table{width:100%;border-collapse:collapse;margin-top:6px}thead{display:table-header-group}tr{break-inside:avoid}th{background:#1e3a5f;color:#fff;text-align:left;padding:6px;font-size:8px;text-transform:uppercase}td{padding:6px;border-bottom:1px solid #e5e7eb;font-size:9px}tbody tr:nth-child(even){background:#f8fafc}
          .footer{text-align:center;border-top:1px solid #dbe3ec;margin-top:13px;padding-top:7px;color:#94a3b8;font-size:8px}
          @media print{body{padding:11mm 12mm}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}}
        </style></head><body>
          <div class="watermark">${safeSchoolName}</div>
          <header class="header"><div><h1>${safeSchoolName}</h1><p>Class Details Report</p></div><div class="meta"><strong>Generated:</strong> ${new Date().toLocaleDateString()}<br><strong>Time:</strong> ${new Date().toLocaleTimeString()}</div></header>
          <h2 class="title">${escapeHtml(classData.name)} — Class Details</h2>
          <div class="summary">
            <div><span>Level</span><strong>${escapeHtml(classData.level || 'N/A')}</strong></div>
            <div><span>Class Number</span><strong>${escapeHtml(classData.class_number || 'N/A')}</strong></div>
            <div><span>Faculty</span><strong>${escapeHtml(classData.faculty || 'General')}</strong></div>
            <div><span>Status</span><strong>${escapeHtml(getStatusLabel(classData.status) || 'N/A')}</strong></div>
            <div><span>Class Teacher</span><strong>${teacherNames}</strong></div>
            <div><span>Created</span><strong>${classData.created_at ? new Date(classData.created_at).toLocaleDateString() : 'N/A'}</strong></div>
          </div>
          <div class="section"><h2>Assigned Subjects (${assignedSubjects.length})</h2><table class="subjects-table"><thead><tr><th>Subject</th><th>Assigned Teacher(s)</th></tr></thead><tbody>${subjectRows || '<tr><td colspan="2" style="text-align:center">No subjects assigned to this class.</td></tr>'}</tbody></table></div>
          <div class="section"><h2>Student Statistics Chart</h2><div class="chart">${chartBars}</div></div>
          <div class="section"><h2>Students (${totalCount})</h2><table><thead><tr><th>#</th><th>Name</th><th>Admission</th><th>Gender</th><th>Email</th></tr></thead><tbody>${studentRows || '<tr><td colspan="5" style="text-align:center">No students enrolled in this class.</td></tr>'}</tbody></table></div>
          <footer class="footer">This is a computer-generated class report from ${safeSchoolName}.</footer>
        </body></html>`;

      const printWindow = window.open('', '_blank', 'width=900,height=800');
      if (!printWindow) {
        showWarning({ text: 'Please allow pop-ups to generate the PDF.' });
        return;
      }
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.document.title = `${schoolName || 'School'} - ${classData.name} Details`;
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    } catch (error) {
      console.error('Class PDF error:', error);
      showError({ text: 'Failed to generate the class details PDF.' });
    } finally {
      setIsPrinting(false);
    }
  };

  const assignedTeacherIds = assignedTeachers.map(t => t.id);
  const availableTeachers = allTeachers.filter(t => !assignedTeacherIds.includes(t.id));
  const filteredAvailableTeachers = availableTeachers.filter(teacher => {
    const search = searchTerm.toLowerCase();
    const fullName = getFullName(teacher).toLowerCase();
    return fullName.includes(search) ||
           (teacher.employee_number && teacher.employee_number.toLowerCase().includes(search)) ||
           (teacher.qualification && teacher.qualification.toLowerCase().includes(search));
  });

  const assignedSubjectIds = assignedSubjects.map(s => s.id);
  const availableSubjects = allSubjects.filter(s => !assignedSubjectIds.includes(s.id));

  const studentTotalPages = Math.max(
    1,
    Math.ceil(students.length / STUDENTS_PER_PAGE)
  );
  const studentStartIndex =
    (studentPage - 1) * STUDENTS_PER_PAGE;
  const visibleStudents = students.slice(
    studentStartIndex,
    studentStartIndex + STUDENTS_PER_PAGE
  );
  const showingFrom = students.length
    ? studentStartIndex + 1
    : 0;
  const showingTo = Math.min(
    studentStartIndex + STUDENTS_PER_PAGE,
    students.length
  );

  const totalStudentCount = students.length;
  const maleStudentCount = students.filter(
    (student) => normalizeGender(student.gender) === 'male'
  ).length;
  const femaleStudentCount = students.filter(
    (student) => normalizeGender(student.gender) === 'female'
  ).length;

  const chartData = [
    {
      name: 'Students',
      Total: totalStudentCount,
      Male: maleStudentCount,
      Female: femaleStudentCount,
    },
  ];

  if (loading) return <FireLoader fullScreen size="lg" text="LOADING..." />;
  if (!classData) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-primary-700">Class not found</h2>
        <button
          onClick={() => navigate('/app/classes')}
          className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
        >
          Back to Classes
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6 max-w-6xl mx-auto px-4 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <button
          onClick={() => navigate('/app/classes')}
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 transition text-sm sm:text-base"
        >
          <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          Back to Classes
        </button>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handlePrintClassDetails}
            disabled={isPrinting}
            className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm disabled:opacity-50"
          >
            <PrinterIcon className="w-4 h-4" />
            <span>{isPrinting ? 'Preparing PDF...' : 'PDF'}</span>
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => navigate(`/app/classes/edit/${classData.id}`)}
                className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm"
              >
                <PencilIcon className="w-4 h-4" />
                <span>Edit</span>
              </button>
              <button
                onClick={handleDeleteClass}
                className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition text-sm"
              >
                <TrashIcon className="w-4 h-4" />
                <span>Delete</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Class Info Card – flat */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <BuildingLibraryIcon className="w-8 h-8 sm:w-10 sm:h-10 text-gray-600" />
          </div>
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800">{classData.name}</h1>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(classData.status)}`}>
                    {getStatusLabel(classData.status)}
                  </span>
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                    {classData.level} - {classData.class_number}
                  </span>
                  {classData.faculty && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                      {classData.faculty}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-left md:text-right">
                <p className="text-xs sm:text-sm text-gray-400">Total Students</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-800">{students.length || 0}</p>
              </div>
            </div>
          </div>
        </div>
        {classData.description && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-sm text-gray-500">{classData.description}</p>
          </div>
        )}
      </div>

      {/* Teachers Section – flat */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-2">
            <UserGroupIcon className="w-5 h-5 text-gray-600" />
            Class Teacher ({assignedTeachers.length})
          </h2>
          {isAdmin && (
            <button
              onClick={() => setShowAssignModal(true)}
              className="inline-flex items-center justify-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-xs sm:text-sm disabled:opacity-50"
            >
              <UserPlusIcon className="w-4 h-4" />
              {assignedTeachers.length > 0 ? 'Replace Teacher' : 'Assign Teacher'}
            </button>
          )}
        </div>

        {assignedTeachers.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {assignedTeachers.map((teacher) => {
              const isRemovingThis = isRemoving && pendingTeacherId === teacher.id;
              return (
                <div
                  key={teacher.id}
                  className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg gap-2 group hover:bg-gray-100 transition ${
                    isRemovingThis ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <UserIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <button
                      type="button"
                      onClick={() => navigate(`/app/teachers/${teacher.id}`)}
                      className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline truncate text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded"
                      title={`View ${getFullName(teacher)}'s details`}
                    >
                      {getFullName(teacher)}
                    </button>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => handleRemoveTeacher(teacher.id)}
                      disabled={isRemoving}
                      className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition flex-shrink-0"
                      title="Remove teacher"
                    >
                      {isRemovingThis ? (
                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <TrashIcon className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 sm:py-8 text-gray-400">
            <UserGroupIcon className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-200 mb-2" />
            <p className="text-sm">No teacher assigned</p>
            {isAdmin && (
              <button
                onClick={() => setShowAssignModal(true)}
                className="mt-2 text-primary-600 hover:text-primary-700 font-medium text-sm"
              >
                Assign a teacher now
              </button>
            )}
          </div>
        )}
      </div>

      {/* Subjects Section – flat */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-2">
            <BookOpenIcon className="w-5 h-5 text-gray-600" />
            Subjects ({assignedSubjects.length})
          </h2>
          {isAdmin && (
            <button
              onClick={() => setShowAssignSubjectModal(true)}
              className="inline-flex items-center justify-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-xs sm:text-sm"
            >
              <PlusIcon className="w-4 h-4" />
              Assign Subjects
            </button>
          )}
        </div>

        {assignedSubjects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {assignedSubjects.map((subject) => {
              const teachers = subjectTeachers[subject.id] || [];
              const isRemovingThis = isRemovingSubject && pendingSubjectId === subject.id;
              return (
                <div
                  key={subject.id}
                  className={`flex flex-col p-3 sm:p-4 bg-gray-50 rounded-lg gap-1 group hover:bg-gray-100 transition ${
                    isRemovingThis ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 text-sm sm:text-base">{subject.name}</p>
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => handleRemoveSubject(subject.id)}
                        disabled={isRemovingSubject}
                        className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition"
                        title="Remove subject"
                      >
                        {isRemovingThis ? (
                          <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <TrashIcon className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                  <div className="mt-1">
                    {teachers.length > 0 ? (
                      <div className="flex flex-wrap items-start gap-1">
                        <div className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap mt-0.5">
                          <UserIcon className="w-3 h-3" />
                          <span className="font-medium">Teacher:</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {teachers.map((teacher) => (
                            <span
                              key={teacher.id}
                              className="inline-flex items-center gap-1 text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full"
                            >
                              {teacher.first_name} {teacher.last_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">No teachers assigned yet</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 sm:py-8 text-gray-400">
            <BookOpenIcon className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-200 mb-2" />
            <p className="text-sm">No subjects assigned</p>
            {isAdmin && (
              <button
                onClick={() => setShowAssignSubjectModal(true)}
                className="mt-2 text-primary-600 hover:text-primary-700 font-medium text-sm"
              >
                Assign subjects now
              </button>
            )}
          </div>
        )}
      </div>

      {/* Info Grid – flat */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Class Information</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <BuildingLibraryIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-xs sm:text-sm text-gray-400">Class Name</p>
                <p className="text-sm sm:text-base font-medium text-gray-800">{classData.name}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <AcademicCapIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-xs sm:text-sm text-gray-400">Level</p>
                <p className="text-sm sm:text-base font-medium text-gray-800 capitalize">{classData.level}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <UserGroupIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-xs sm:text-sm text-gray-400">Class Number</p>
                <p className="text-sm sm:text-base font-medium text-gray-800">{classData.class_number}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <BriefcaseIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-xs sm:text-sm text-gray-400">Faculty</p>
                <p className="text-sm sm:text-base font-medium text-gray-800 capitalize">{classData.faculty || 'General'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CalendarIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-xs sm:text-sm text-gray-400">Created</p>
                <p className="text-sm sm:text-base font-medium text-gray-800">
                  {classData.created_at ? new Date(classData.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Student Statistics</h2>
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-2 text-center sm:p-3">
              <p className="text-lg font-bold text-indigo-600 sm:text-xl">{totalStudentCount}</p>
              <p className="text-[10px] text-indigo-500 sm:text-xs">Total</p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-2 text-center sm:p-3">
              <p className="text-lg font-bold text-green-600 sm:text-xl">
                {maleStudentCount}
              </p>
              <p className="text-[10px] text-green-500 sm:text-xs">Male</p>
            </div>
            <div className="rounded-lg border border-pink-200 bg-pink-50 p-2 text-center sm:p-3">
              <p className="text-lg font-bold text-pink-600 sm:text-xl">
                {femaleStudentCount}
              </p>
              <p className="text-[10px] text-pink-500 sm:text-xs">Female</p>
            </div>
          </div>
          <div className="h-48 sm:h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }} barSize={30}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Total" fill={STUDENT_STAT_COLORS.total} />
                <Bar dataKey="Male" fill={STUDENT_STAT_COLORS.male} />
                <Bar dataKey="Female" fill={STUDENT_STAT_COLORS.female} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ===== Students Table – responsive with horizontal scroll ===== */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-2">
            <UserGroupIcon className="w-5 h-5 text-gray-600" />
            Students
          </h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate(`/app/classes/${id}/attendance`)}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-xs sm:text-sm"
            >
              <CalendarIcon className="w-4 h-4" />
              <span>View Attendance</span>
            </button>
            {(isAdmin || isTeacher) && (
              <button
                onClick={() => navigate(`/app/classes/${id}/attendance/mark`)}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-xs sm:text-sm"
              >
                <CheckIcon className="w-4 h-4" />
                <span>Mark Attendance</span>
              </button>
            )}
          </div>
        </div>

        {students.length > 0 ? (
          <>
            {/* Single table with horizontal scroll on small screens */}
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="inline-block min-w-full align-middle px-4 sm:px-0">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-500">#</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-500">Name</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-500">Admission</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-500">Gender</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-500">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleStudents.map((student, index) => {
                      const fullName = formatFullName(student);
                      return (
                        <tr key={student.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                          <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                            {studentStartIndex + index + 1}
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">
                            <button
                              onClick={() => navigate(`/app/students/${student.id}`)}
                              className="hover:text-primary-700 hover:underline transition-colors text-left"
                            >
                              {fullName}
                            </button>
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                            {student.admission_number}
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-500 capitalize whitespace-nowrap">
                            {student.gender}
                          </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                            {student.email || 'N/A'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-500">
                Showing <span className="font-semibold text-gray-700">{showingFrom}–{showingTo}</span>{' '}
                out of <span className="font-semibold text-gray-700">{students.length}</span> students
              </p>

              {studentTotalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setStudentPage((page) => Math.max(page - 1, 1))}
                    disabled={studentPage === 1}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="min-w-[90px] text-center text-sm text-gray-600">
                    Page {studentPage} of {studentTotalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setStudentPage((page) => Math.min(page + 1, studentTotalPages))}
                    disabled={studentPage === studentTotalPages}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-6 sm:py-8 text-gray-400">
            <UserGroupIcon className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-200 mb-2" />
            <p className="text-sm">No students enrolled in this class</p>
          </div>
        )}
      </div>

      {/* ===== Assign Teacher Modal (Admin only) – flat ===== */}
      {isAdmin && showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {assignedTeachers.length > 0 ? 'Replace Teacher' : 'Assign Teacher'}
                </h3>
                <p className="text-sm text-gray-500">
                  {assignedTeachers.length > 0
                    ? 'This class already has a teacher. You can replace them.'
                    : 'Select the homeroom teacher'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedTeacherId(null);
                  setSearchTerm('');
                }}
                className="p-1 hover:bg-gray-100 rounded-lg transition"
              >
                <XMarkIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {assignedTeachers.length > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="font-medium">Class already has a teacher</p>
                  <p className="text-xs text-yellow-600">Assigning a new teacher will replace the current one.</p>
                </div>
              </div>
            )}

            <div className="flex-shrink-0 mb-4">
              <input
                type="text"
                placeholder="Search teachers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50 text-gray-700 text-sm"
              />
              <p className="text-xs text-gray-400 mt-2">
                {selectedTeacherId ? '1 teacher selected' : 'No teacher selected'}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 mb-4">
              {filteredAvailableTeachers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <UserIcon className="w-12 h-12 mx-auto text-gray-200 mb-2" />
                  <p>No available teachers to assign</p>
                  <p className="text-sm">All teachers are already assigned to this class</p>
                </div>
              ) : (
                filteredAvailableTeachers.map((teacher) => (
                  <label
                    key={teacher.id}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                      selectedTeacherId === teacher.id
                        ? 'bg-primary-50 border-2 border-primary-300'
                        : 'hover:bg-gray-50 border-2 border-transparent'
                    }`}
                  >
                    <input
                      type="radio"
                      name="teacherSelection"
                      value={teacher.id}
                      checked={selectedTeacherId === teacher.id}
                      onChange={() => setSelectedTeacherId(teacher.id)}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      {teacher.user?.profile_picture ? (
                        <img
                          src={teacher.user.profile_picture}
                          alt={getFullName(teacher)}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-gray-600 font-bold text-sm">
                          {getInitials(teacher.first_name, teacher.last_name)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 text-sm">{getFullName(teacher)}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                        <span>#{teacher.employee_number}</span>
                        {teacher.qualification && (
                          <>
                            <span>•</span>
                            <span>{teacher.qualification}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>

            <div className="flex-shrink-0 flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedTeacherId(null);
                  setSearchTerm('');
                }}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-700 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignTeacher}
                disabled={isAssigning || !selectedTeacherId}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              >
                {isAssigning ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <CheckIcon className="w-5 h-5" />
                    {assignedTeachers.length > 0 ? 'Replace Teacher' : 'Assign Teacher'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Assign Subjects Modal (Admin only) – flat ===== */}
      {isAdmin && showAssignSubjectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Assign Subjects</h3>
                <p className="text-sm text-gray-500">Select subjects to assign to this class</p>
              </div>
              <button
                onClick={() => {
                  setShowAssignSubjectModal(false);
                  setSelectedSubjectIds([]);
                }}
                className="p-1 hover:bg-gray-100 rounded-lg transition"
              >
                <XMarkIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="flex-shrink-0 mb-4">
              <p className="text-xs text-gray-400">{selectedSubjectIds.length} subject(s) selected</p>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 mb-4">
              {availableSubjects.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <BookOpenIcon className="w-12 h-12 mx-auto text-gray-200 mb-2" />
                  <p>No available subjects to assign</p>
                  <p className="text-sm">All subjects are already assigned</p>
                </div>
              ) : (
                availableSubjects.map((subject) => (
                  <label
                    key={subject.id}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                      selectedSubjectIds.includes(subject.id)
                        ? 'bg-primary-50 border-2 border-primary-300'
                        : 'hover:bg-gray-50 border-2 border-transparent'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSubjectIds.includes(subject.id)}
                      onChange={() => {
                        setSelectedSubjectIds(prev =>
                          prev.includes(subject.id)
                            ? prev.filter(id => id !== subject.id)
                            : [...prev, subject.id]
                        );
                      }}
                      className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 text-sm">{subject.name}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                        <span className="font-mono">{subject.code || '—'}</span>
                        <span>•</span>
                        <span className="capitalize">{subject.level || '—'}</span>
                        {subject.subject_type && (
                          <>
                            <span>•</span>
                            <span className="capitalize">{subject.subject_type}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
            <div className="flex-shrink-0 flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowAssignSubjectModal(false);
                  setSelectedSubjectIds([]);
                }}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-700 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignSubjects}
                disabled={isAssigningSubjects || selectedSubjectIds.length === 0}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              >
                {isAssigningSubjects ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <CheckIcon className="w-5 h-5" />
                    Assign {selectedSubjectIds.length} Subject{selectedSubjectIds.length > 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}